(() => {
  const PRODUCT_SEARCH_FIELDS = [
    'title',
    'body',
    'product_type',
    'tag',
    'variants.title',
    'variants.sku',
    'variants.barcode',
    'vendor'
  ];

  const isStructuredProductQuery = query =>
    query.startsWith('(title:') && query.includes(' OR body:');

  const hasAdvancedSearchSyntax = query =>
    /(^|\s)(AND|OR|NOT)(\s|$)/i.test(query) || /(^|\s)[a-z_][\w.]*:/i.test(query);

  const escapeSearchToken = token => token.replace(/([\\:()])/g, '\\$1');

  const buildProductSearchQuery = searchTerm => {
    const normalizedTerm = searchTerm.trim();

    if (
      !normalizedTerm ||
      isStructuredProductQuery(normalizedTerm) ||
      hasAdvancedSearchSyntax(normalizedTerm)
    ) {
      return normalizedTerm;
    }

    const tokens = normalizedTerm
      .split(/\s+/)
      .filter(Boolean)
      .map(escapeSearchToken);

    return tokens
      .map(
        token =>
          `(${PRODUCT_SEARCH_FIELDS.map(field => `${field}:${token}*`).join(' OR ')})`
      )
      .join(' AND ');
  };

  const getDisplaySearchTerm = query => {
    if (!isStructuredProductQuery(query)) {
      return query;
    }

    return query
      .split(') AND (')
      .map(group => {
        const match = group.match(/(?:^\()?title:([^*]+)\*/);
        return match ? match[1].replace(/\\([\\:()])/g, '$1') : '';
      })
      .filter(Boolean)
      .join(' ');
  };

  const normalizeSearchPageUrl = () => {
    const pathname = window.location.pathname.replace(/\/+$/, '');

    if (!pathname.endsWith('/search')) {
      return;
    }

    const url = new URL(window.location.href);
    const currentQuery = (url.searchParams.get('q') || '').trim();

    if (!currentQuery) {
      return;
    }

    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'last');
    url.searchParams.set('options[prefix]', 'last');

    if (isStructuredProductQuery(currentQuery)) {
      url.searchParams.set('q', getDisplaySearchTerm(currentQuery));
      window.history.replaceState(window.history.state, '', url);
      return;
    }

    if (hasAdvancedSearchSyntax(currentQuery)) {
      if (url.href !== window.location.href) {
        window.location.replace(url);
      }
      return;
    }

    url.searchParams.set('q', buildProductSearchQuery(currentQuery));
    url.searchParams.delete('page');
    window.location.replace(url);
  };

  normalizeSearchPageUrl();

  if (customElements.get('predictive-search')) {
    return;
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('.js-results-container');
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector('.js-message-error');
      this.errorMessageText = this.errorMessageContainer?.querySelector('[data-text]');
      this.timeout = null;
      this.abortController = null;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');
      this.searchDrawerContent = this.closest('.drawer__content--search');
      this.searchSuggestions = this.searchDrawerContent?.querySelector(
        '.search-drawer__suggestions'
      );

      this.form?.addEventListener('submit', event => {
        const searchTerm = this.input.value.trim();

        if (!searchTerm) {
          event.preventDefault();
        }
      });

      this.form?.addEventListener('formdata', event => {
        const searchTerm = this.input.value.trim();

        if (searchTerm) {
          event.formData.set('q', buildProductSearchQuery(searchTerm));
        }
      });

      this.addEventListener('keydown', event => this.handleKeyboardNavigation(event));

      if (!this.resultsContainer) {
        return;
      }

      this.input.addEventListener('input', () => {
        clearTimeout(this.timeout);
        this.abortController?.abort();
        this.isOpen = false;
        this.classList.remove('is-open');
        this.resultsContainer.classList.add('hidden');
        this.input.setAttribute('aria-expanded', 'false');

        const searchTerm = this.input.value.trim();

        if (!searchTerm) {
          this.close();
          this.setDrawerSearchState(false);
          return;
        }

        this.timeout = setTimeout(() => {
          this.isOpen = true;
          this.getSearchResults(searchTerm);
          this.setDrawerSearchState(true);
        }, 300);
      });

      this.btnClearText?.addEventListener('click', () => {
        this.close();
        this.setDrawerSearchState(false);
      });
    }

    handleKeyboardNavigation(event) {
      if (event.key === 'Escape') {
        if (!this.input.value && this.drawer) {
          this.drawer.parentElement.parentElement.toggleDrawer();
          return;
        }

        this.close();
        return;
      }

      if (!this.isOpen || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) {
        return;
      }

      const focusableResults = Array.from(
        this.resultsContainer.querySelectorAll('a[href], button:not([disabled])')
      ).filter(element => element.getClientRects().length > 0);

      if (!focusableResults.length) {
        return;
      }

      event.preventDefault();

      const currentIndex = focusableResults.indexOf(document.activeElement);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? direction === 1
            ? 0
            : focusableResults.length - 1
          : (currentIndex + direction + focusableResults.length) % focusableResults.length;

      focusableResults[nextIndex].focus();
    }

    setDrawerSearchState(hasResults) {
      if (
        this.searchDrawerContent &&
        (DeviceDetector.isMobile() || DeviceDetector.isTablet())
      ) {
        this.searchDrawerContent.style.height = hasResults ? '100%' : 'auto';
      }

      if (this.searchSuggestions) {
        this.searchSuggestions.style.display = hasResults ? 'none' : 'block';
      }
    }

    async getSearchResults(searchTerm) {
      this.abortController?.abort();

      const controller = new AbortController();
      this.abortController = controller;

      const params = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': PRODUCT_SEARCH_FIELDS.join(','),
        section_id: 'predictive-search'
      });

      try {
        const response = await fetch(
          `${Shopify.routes.root}search/suggest?${params.toString()}`,
          { signal: controller.signal }
        );

        if (!this.isOpen || this.abortController !== controller) {
          return;
        }

        if (!response.ok) {
          throw new Error(response.status);
        }

        const text = await response.text();
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!resultsSection) {
          throw new Error('Invalid predictive search response');
        }

        this.resultsContainer.innerHTML = resultsSection.innerHTML;
        this.open();

        document.querySelector('quick-cart-drawer')?.initTriggers();
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (this.errorMessageText) {
          this.errorMessageText.textContent = error.message;
        }
        this.errorMessageContainer?.classList.remove('hidden');
      } finally {
        if (this.abortController === controller) {
          this.abortController = null;
        }
      }
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.btnSubmit?.setAttribute('tabindex', '-1');
      this.btnClearText?.removeAttribute('disabled');
    }

    close() {
      this.abortController?.abort();
      this.abortController = null;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer?.classList.add('hidden');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.value = '';
      this.input.focus();
      this.btnSubmit?.removeAttribute('tabindex');
      this.btnClearText?.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
